'use client'

import { useState, useEffect, useCallback } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBars, faTimes, faDice } from '@fortawesome/free-solid-svg-icons'
import { usePathname } from 'next/navigation'
import { BRAND } from '@/config/domain.config'
import { menuItems, MenuItem } from '@/config/menu.config'
import { useExtensionCleanup } from '@/hooks/useExtensionCleanup'

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const pathname = usePathname()
  
  // Use extension cleanup hook
  useExtensionCleanup()

  // Handle closing menu
  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false)
    document.body.style.overflow = ''
  }, [])

  // Close menu when route changes
  useEffect(() => {
    closeMobileMenu()
  }, [pathname, closeMobileMenu])

  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const menu = document.querySelector('.mobile-menu')
      const toggle = document.querySelector('.mobile-toggle')
      if (
        isMobileMenuOpen &&
        menu &&
        toggle &&
        !menu.contains(e.target as Node) &&
        !toggle.contains(e.target as Node)
      ) {
        closeMobileMenu()
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [isMobileMenuOpen, closeMobileMenu])

  // Toggle body scroll
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : ''
  }, [isMobileMenuOpen])

  const isActive = (href: string) => {
    if (href === '/') {
      return pathname === href
    }
    return pathname.startsWith(href)
  }

  const BettingButton = ({ isMobile = false }) => (
    <a
      href="https://okwintv01.okfun.vin/register.html"
      target="_blank"
      rel="noopener noreferrer"
      className={`btn-cakhiatv flex items-center gap-2 ${
        isMobile 
          ? 'text-xs'
          : 'text-sm'
      }`}
    >
      {/* <span className={`flex items-center justify-center animate-spin-slow group-hover:animate-spin ${
        isMobile ? 'w-4 h-4' : 'w-5 h-5'
      }`}>
        <FontAwesomeIcon icon={faDice} className={isMobile ? 'text-base' : 'text-xl'} />
      </span> */}
      <span className={`font-semibold uppercase tracking-wider ${
        isMobile ? 'text-xs' : 'text-sm'
      }`}>
        {isMobile ? 'cược okwintv' : 'cược okwintv'}
      </span>
    </a>
  )

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-white/5 backdrop-blur-lg bg-opacity-95" suppressHydrationWarning>
        <div className="container mx-auto px-4" suppressHydrationWarning>
        <nav className="flex items-center justify-between h-[55px] lg:h-[60px]" suppressHydrationWarning>
            {/* Logo - Left */}
            <Link href="/" className="header__logo">
              <Image
                src="/images/logo.png"
                alt={BRAND.NAME}
                width={200}
                height={66}
                className="w-[140px] md:w-[180px] h-auto"
                quality={100}
              />
            </Link>

            {/* Desktop Menu - Center (only from lg) */}
            <div className="hidden lg:flex items-center gap-1 absolute left-1/2 transform -translate-x-1/2">
              {menuItems.map((item: MenuItem) => {
                const active = isActive(item.href)
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={`group relative flex items-center gap-1.5 px-3 py-2 text-[13px] font-semibold whitespace-nowrap transition-all ${
                      active 
                        ? 'text-[var(--primary)] bg-[rgba(var(--primary-rgb),0.1)] rounded-t-lg' 
                        : 'text-white hover:text-[var(--primary)] hover:bg-[rgba(var(--primary-rgb),0.1)] hover:rounded-t-lg'
                    }`}
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <FontAwesomeIcon
                        icon={item.icon}
                        className={`transition-colors ${
                          active 
                            ? 'text-[var(--primary)]' 
                            : 'text-gray-400 group-hover:text-[var(--primary)]'
                        }`}
                        fixedWidth
                      />
                    </span>
                    {item.text}
                    {active && (
                      <span className="absolute bottom-0 left-0 w-full h-0.5 bg-[var(--primary)]" />
                    )}
                  </Link>
                )
              })}
            </div>

            {/* Betting Button - Right (only from lg) */}
            <div className="hidden lg:flex items-center">
              <div className="hidden lg:block">
                <BettingButton isMobile={false} />
              </div>
              <div className="hidden md:block lg:hidden">
                <BettingButton isMobile={true} />
              </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center gap-2">
              <BettingButton isMobile={true} />
              <button
                className="mobile-toggle w-10 h-10 flex items-center justify-center text-white" aria-label="Mở menu điều hướng"
                onClick={(e) => {
                  e.stopPropagation()
                  setIsMobileMenuOpen(!isMobileMenuOpen)
                }}
              >
                <FontAwesomeIcon icon={isMobileMenuOpen ? faTimes : faBars} className="text-2xl" fixedWidth />
              </button>
            </div>
          </nav>
        </div>
      </header>

      {/* Bottom header marquee banner */}
      {/* <div className="hidden lg:block w-full overflow-hidden py-2" style={{ backgroundColor: '#f74949' }}>
        <div className="animate-marquee-single whitespace-nowrap">
          <span className="text-white">
            Quý đối tác hợp tác quảng cáo vui lòng liên hệ qua mail chính chủ <a style={{ color: 'yellow' }} target="_blank" href="#">Contact@OkwinTV.TOP</a> . Tất cả mọi hình thức liên hệ qua telegram, facebook, zalo của BLV đều là giả mạo. Rất nhiều đối tác đã bị lừa khi liên hệ với BLV để hợp tác giao dịch...
          </span>
        </div>
      </div> */}

      {/* Mobile Menu (visible up to lg) */}
      <div
        className={`mobile-menu fixed inset-0 z-40 bg-[#1a1a1a] bg-opacity-95 backdrop-blur-lg transition-transform duration-300 lg:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pt-20 px-4">
          {menuItems.map((item: MenuItem) => {
            const active = isActive(item.href)
            return (
              <Link
                key={item.href}
                href={item.href}
                onClick={closeMobileMenu}
                className={`flex items-center gap-3 px-4 py-3 text-[15px] font-medium border-b border-white/5 ${
                  active ? 'text-[var(--primary)]' : 'text-white'
                }`}
              >
                <FontAwesomeIcon
                  icon={item.icon}
                  className={active ? 'text-[var(--primary)]' : 'text-gray-400'}
                  fixedWidth
                />
                {item.text}
              </Link>
            )
          })}
        </div>
      </div>

      <style jsx>{`
        .animate-marquee-single {
          display: inline-block;
          padding-left: 100%;
          animation: marquee-left 25s linear infinite;
        }
        @keyframes marquee-left {
          0% { transform: translateX(0); }
          100% { transform: translateX(-100%); }
        }
      `}</style>
    </>
  )
}